import type { Locale } from '@/i18n/locale'
import type {
  CaseVisual,
  CompareRow,
  LedgerRow,
  SystemNode,
  Tone,
  VisualSlot,
} from '@/data/case-visual-types'

/**
 * Иллюстрации страниц кейсов: одна обёртка + 13 видов.
 *
 * Почему вёрстка, а не PNG: присланные макеты (снимки окна чата, 4,3 МБ на
 * 12 картинок) на ширине статьи сжимались до нечитаемого кегля, были только
 * на русском и показывали название клиента. Здесь текст настоящий, цвета —
 * токены темы (светлая и тёмная из src/styles/tokens.css), ничего не грузится.
 *
 * Правила построения (скилл dataviz):
 *   - полосы тонкие, скругление 4 px только на конце данных, начало — одна база;
 *   - у каждой полосы подпись и значение — цвет не единственный носитель смысла;
 *   - значения и подписи — цветом текста, а не цветом серии;
 *   - палитра accent / muted(65 %) / danger проверена validate_palette.js в обеих
 *     темах: различимость при дальтонизме и контраст ≥ 3:1 — проходят.
 * Анимация роста — только CSS (scroll-driven), только без reduced motion.
 */

const ASK = { ru: 'Запрос', en: 'Prompt' } as const

const FILL: Record<Tone, string> = {
  base: 'bg-muted/65',
  accent: 'bg-accent',
  loss: 'bg-danger',
}

const MONO = 'font-mono text-[0.7rem] uppercase tracking-[0.12em]'

function pct(value: number, max: number) {
  if (max <= 0) return 0
  return Math.max(0, Math.min(100, (value / max) * 100))
}

/** Горизонтальная полоса: начало — общая база, конец скруглён. */
function Bar({ width, tone = 'accent', thin = false }: { width: number; tone?: Tone; thin?: boolean }) {
  return (
    <div className={`${thin ? 'h-2' : 'h-3'} w-full`} aria-hidden>
      <div
        className={`cv-grow-x h-full rounded-r-[4px] ${FILL[tone]}`}
        style={{ width: width > 0 ? `${Math.max(width, 1.5)}%` : 0 }}
      />
    </div>
  )
}

function Check({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" className={`h-3.5 w-3.5 shrink-0 ${className}`} aria-hidden fill="none">
      <path d="M3.5 8.5l3 3 6-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function Arrow({ down = false }: { down?: boolean }) {
  return (
    <svg
      viewBox="0 0 16 16"
      className={`h-4 w-4 text-accent ${down ? 'rotate-90' : ''}`}
      aria-hidden
      fill="none"
    >
      <path d="M2 8h11M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

/* ——————————————————————— виды ——————————————————————— */

function Compare({ v }: { v: Extract<CaseVisual, { kind: 'compare' }> }) {
  const shared = v.scale === 'shared' ? Math.max(...v.rows.flatMap((r) => [r.before, r.after])) : 0
  const max = (r: CompareRow) => shared || Math.max(r.before, r.after)
  return (
    <ul className="space-y-7">
      {v.rows.map((r) => (
        <li key={r.label}>
          <div className="flex items-baseline justify-between gap-4">
            <span className="text-body text-ink font-medium leading-snug">{r.label}</span>
            {r.delta && <span className="font-mono text-small text-accent whitespace-nowrap">{r.delta}</span>}
          </div>
          <div className="mt-3 grid grid-cols-[3.75rem_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 border-l border-divider pl-3">
            <span className={`${MONO} text-muted`}>{v.legend[0]}</span>
            <Bar width={pct(r.before, max(r))} tone="base" />
            <span className="text-small text-muted tabular-nums whitespace-nowrap">{r.beforeText}</span>
            <span className={`${MONO} text-accent`}>{v.legend[1]}</span>
            <Bar width={pct(r.after, max(r))} tone="accent" />
            <span className="text-small text-ink font-semibold tabular-nums whitespace-nowrap">{r.afterText}</span>
          </div>
        </li>
      ))}
    </ul>
  )
}

function Bars({ v }: { v: Extract<CaseVisual, { kind: 'bars' }> }) {
  const max = v.max ?? Math.max(...v.rows.map((r) => r.value))
  return (
    <ul className="divide-y divide-divider">
      {v.rows.map((r) => (
        <li key={r.label} className="grid gap-x-5 gap-y-2 py-3.5 first:pt-0 last:pb-0 sm:grid-cols-[12rem_minmax(0,1fr)]">
          <div className="text-small text-ink leading-snug">
            {r.label}
            {r.sub && <span className="block text-muted">{r.sub}</span>}
          </div>
          <div>
            <div className="grid grid-cols-[minmax(0,1fr)_3.75rem] items-center gap-3">
              <Bar width={pct(r.value, max)} tone={r.tone ?? 'base'} />
              <span className="text-small text-ink font-semibold tabular-nums text-right whitespace-nowrap">{r.text}</span>
            </div>
            {r.note && <p className="mt-1.5 text-small text-muted leading-snug">{r.note}</p>}
          </div>
        </li>
      ))}
    </ul>
  )
}

function Columns({ v }: { v: Extract<CaseVisual, { kind: 'columns' }> }) {
  const max = Math.max(...v.rows.map((r) => r.value))
  return (
    <div>
      {v.flag && (
        <p className="mb-4 inline-flex items-center gap-2 rounded-full border border-danger/40 px-3 py-1 text-small text-danger">
          <span className="h-1.5 w-1.5 rounded-full bg-danger" aria-hidden />
          {v.flag}
        </p>
      )}
      <ul className="flex items-end justify-around gap-3 border-b border-divider" style={{ height: '13rem' }}>
        {v.rows.map((r) => (
          <li key={r.label} className="flex h-full min-w-0 flex-1 flex-col items-center justify-end">
            <span className="mb-2 text-small text-ink font-semibold tabular-nums whitespace-nowrap">{r.text}</span>
            <div
              className={`cv-grow-y w-6 rounded-t-[4px] ${FILL[r.tone ?? 'accent']}`}
              style={{ height: `${Math.max(pct(r.value, max) * 0.74, 2)}%` }}
              aria-hidden
            />
          </li>
        ))}
      </ul>
      <ul className="mt-2 flex justify-around gap-3">
        {v.rows.map((r) => (
          <li key={r.label} className="min-w-0 flex-1 text-center text-small text-muted leading-snug">
            {r.label}
          </li>
        ))}
      </ul>
    </div>
  )
}

function Timeline({ v }: { v: Extract<CaseVisual, { kind: 'timeline' }> }) {
  return (
    <ol
      className="grid gap-5 border-l border-divider pl-5 sm:border-l-0 sm:pl-0 sm:gap-4 sm:grid-flow-col sm:auto-cols-fr sm:border-t sm:pt-5"
    >
      {v.steps.map((s) => (
        <li key={s.when + s.title} className="relative">
          <span
            className="absolute -left-[1.6rem] top-1 h-2.5 w-2.5 rounded-full border-2 border-bg bg-accent sm:left-0 sm:-top-[1.6rem]"
            aria-hidden
          />
          <div className={`${MONO} text-accent`}>{s.when}</div>
          <div className="mt-1.5 text-small text-ink font-semibold leading-snug">{s.title}</div>
          {s.text && <p className="mt-1 text-small text-muted leading-snug">{s.text}</p>}
        </li>
      ))}
    </ol>
  )
}

function Transform({ v }: { v: Extract<CaseVisual, { kind: 'transform' }> }) {
  return (
    <div>
      <div className={`hidden sm:grid grid-cols-[minmax(0,1fr)_1.5rem_minmax(0,1fr)] gap-3 mb-3 ${MONO}`} aria-hidden>
        <span className="text-muted">{v.legend[0]}</span>
        <span />
        <span className="text-accent">{v.legend[1]}</span>
      </div>
      <ul className="space-y-4">
        {v.rows.map((r) => (
          <li key={r.label}>
            <div className="text-small text-ink font-semibold mb-1.5">{r.label}</div>
            <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_1.5rem_minmax(0,1fr)] sm:items-stretch sm:gap-3">
              <p className="rounded-btn border border-divider bg-bg px-3.5 py-2.5 text-small text-muted leading-snug">
                <span className="sr-only">{v.legend[0]}: </span>
                {r.before}
              </p>
              <span className="flex items-center justify-center">
                <span className="sm:hidden">
                  <Arrow down />
                </span>
                <span className="hidden sm:inline">
                  <Arrow />
                </span>
              </span>
              <p className="rounded-btn border border-accent/35 bg-accent/[0.07] px-3.5 py-2.5 text-small text-ink leading-snug">
                <span className="sr-only">{v.legend[1]}: </span>
                {r.after}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

const ACCESS: Record<SystemNode['access'], string> = {
  read: 'border-divider text-muted',
  confirm: 'border-accent/45 text-accent',
  write: 'border-accent bg-accent text-bg',
}

function Systems({ v }: { v: Extract<CaseVisual, { kind: 'systems' }> }) {
  return (
    <div>
      <div className="flex flex-col items-center">
        <span className="inline-flex items-center gap-2 rounded-full border border-accent/45 bg-accent/[0.08] px-4 py-1.5 font-mono text-small text-accent">
          <span className="h-2 w-2 rounded-full bg-accent" aria-hidden />
          {v.hub}
        </span>
        <span className="h-5 w-px bg-divider" aria-hidden />
      </div>
      <ul className="grid gap-3 border-t border-divider pt-5 sm:grid-cols-2">
        {v.nodes.map((n) => (
          <li key={n.name} className="rounded-btn border border-divider bg-bg p-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <span className="text-small text-ink font-semibold">{n.name}</span>
              <span className={`rounded-full border px-2 py-0.5 font-mono text-[0.68rem] leading-5 ${ACCESS[n.access]}`}>
                {n.mode}
              </span>
            </div>
            <p className="mt-1.5 text-small text-muted leading-snug">{n.does}</p>
          </li>
        ))}
      </ul>
      {v.rules && (
        <ul className="mt-5 grid gap-2 sm:grid-cols-3">
          {v.rules.map((r) => (
            <li key={r} className="flex gap-2 text-small text-ink leading-snug">
              <Check className="mt-1 text-accent" />
              <span>{r}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function Dialog({ v }: { v: Extract<CaseVisual, { kind: 'dialog' }> }) {
  const hasAside = Boolean(v.aside?.length || v.chips?.length)
  return (
    <div className={hasAside ? 'grid gap-5 md:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]' : ''}>
      <ol className="space-y-3">
        {v.messages.map((m, i) => (
          <li key={i} className={m.side === 'ai' ? 'flex justify-end' : 'flex'}>
            <div
              className={
                m.side === 'ai'
                  ? 'max-w-[88%] rounded-2xl rounded-br-sm border border-accent/30 bg-accent/[0.08] px-4 py-3'
                  : 'max-w-[88%] rounded-2xl rounded-bl-sm border border-divider bg-bg px-4 py-3'
              }
            >
              <div className={`${MONO} ${m.side === 'ai' ? 'text-accent' : 'text-muted'}`}>{m.who}</div>
              <p className="mt-1 text-small text-ink leading-relaxed">{m.text}</p>
            </div>
          </li>
        ))}
      </ol>
      {hasAside && (
        <div className="space-y-4">
          {v.chips && (
            <ul className="flex flex-wrap gap-2">
              {v.chips.map((c) => (
                <li key={c} className="rounded-full border border-divider bg-bg px-3 py-1 font-mono text-[0.72rem] text-ink tabular-nums">
                  {c}
                </li>
              ))}
            </ul>
          )}
          {v.aside && (
            <div className="rounded-btn border border-divider bg-bg p-4">
              {v.asideTitle && <div className={`${MONO} text-muted`}>{v.asideTitle}</div>}
              <ul className="mt-3 space-y-2.5">
                {v.aside.map((a) => (
                  <li key={a} className="flex gap-2 text-small text-ink leading-snug">
                    <Check className="mt-1 text-accent" />
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const TAG: Record<LedgerRow['tone'], string> = {
  done: 'border-accent bg-accent text-bg',
  calc: 'border-accent/50 text-accent',
  potential: 'border-divider text-ink',
  goal: 'border-ink/40 text-ink',
  other: 'border-divider bg-divider/60 text-muted',
}

function Ledger({ v }: { v: Extract<CaseVisual, { kind: 'ledger' }> }) {
  return (
    <ul className="divide-y divide-divider">
      {v.rows.map((r) => (
        <li key={r.what} className="grid gap-x-4 gap-y-1.5 py-3 first:pt-0 last:pb-0 sm:grid-cols-[7.5rem_minmax(0,1fr)_auto] sm:items-baseline">
          <span className={`justify-self-start rounded-full border px-2.5 py-0.5 font-mono text-[0.68rem] leading-5 ${TAG[r.tone]}`}>
            {r.tag}
          </span>
          <span className="text-small text-ink leading-snug">{r.what}</span>
          <span className="text-small text-ink font-semibold tabular-nums sm:text-right">{r.amount}</span>
        </li>
      ))}
    </ul>
  )
}

function Tiles({ v }: { v: Extract<CaseVisual, { kind: 'tiles' }> }) {
  return (
    <ul className="grid grid-cols-2 gap-px overflow-hidden rounded-btn border border-divider bg-divider md:grid-cols-3">
      {v.tiles.map((t) => (
        <li key={t.label} className="bg-bg p-3.5 sm:p-4">
          <div className={`${MONO} text-muted`}>{t.label}</div>
          <div className={`mt-2 font-serif text-[1.35rem] sm:text-[1.65rem] leading-none ${t.tone === 'loss' ? 'text-danger' : 'text-ink'}`}>
            {t.value}
          </div>
          {t.meter !== undefined && (
            <div className="mt-3 h-1.5 w-full rounded-full bg-divider" aria-hidden>
              <div
                className={`cv-grow-x h-full rounded-full ${FILL[t.tone ?? 'accent']}`}
                style={{ width: `${pct(t.meter, 100)}%` }}
              />
            </div>
          )}
          <p className="mt-2 text-small text-muted leading-snug">{t.text}</p>
        </li>
      ))}
    </ul>
  )
}

function Verify({ v }: { v: Extract<CaseVisual, { kind: 'verify' }> }) {
  return (
    <div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-btn border border-divider bg-bg p-4">
          <div className={`${MONO} text-muted`}>{v.wrong.label}</div>
          <div className="mt-2 font-serif text-[1.85rem] leading-none text-muted line-through decoration-danger decoration-2">
            {v.wrong.value}
          </div>
          <p className="mt-2 text-small text-muted leading-snug">{v.wrong.text}</p>
        </div>
        <div className="rounded-btn border border-accent/50 bg-accent/[0.07] p-4">
          <div className={`${MONO} text-accent`}>{v.right.label}</div>
          <div className="mt-2 font-serif text-[1.85rem] leading-none text-ink">{v.right.value}</div>
          <p className="mt-2 text-small text-ink leading-snug">{v.right.text}</p>
        </div>
      </div>
      <div className={`mt-6 ${MONO} text-muted`}>{v.checksTitle}</div>
      <ul className="mt-3 space-y-2.5">
        {v.checks.map((c) => (
          <li key={c} className="grid grid-cols-[8.5rem_minmax(0,1fr)_auto] items-center gap-3 text-small">
            <span className="text-ink">{c}</span>
            <Bar width={100} tone="accent" thin />
            <span className="inline-flex items-center gap-1 font-mono text-[0.7rem] text-accent">
              <Check />
              {v.ok}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function Memo({ v }: { v: Extract<CaseVisual, { kind: 'memo' }> }) {
  return (
    <div className="grid gap-5 md:grid-cols-2">
      <div>
        <div className={`${MONO} text-muted`}>{v.listTitle}</div>
        <ul className="mt-3 space-y-3">
          {v.items.map((i) => (
            <li key={i} className="flex gap-2.5 text-small text-ink leading-snug">
              <span
                className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-danger font-mono text-[0.6rem] font-bold text-bg"
                aria-hidden
              >
                !
              </span>
              <span>{i}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="rounded-btn border border-divider bg-bg p-4">
        <div className={`${MONO} text-accent`}>{v.quoteTitle}</div>
        <p className="mt-2 text-small text-ink leading-relaxed">{v.quote}</p>
        {v.quoteNote && <p className="mt-3 border-t border-divider pt-3 text-small text-muted leading-snug">{v.quoteNote}</p>}
      </div>
    </div>
  )
}

function Units({ v }: { v: Extract<CaseVisual, { kind: 'units' }> }) {
  return (
    <ul className="space-y-6">
      {v.groups.map((g) => (
        <li key={g.label}>
          <div className="flex items-baseline gap-3">
            <span className="font-serif text-[2.25rem] leading-none text-ink">{g.count}</span>
            <span className="text-small text-ink leading-snug">{g.label}</span>
          </div>
          {/* Чем меньше единиц, тем крупнее каждая: 8 франчайзи читаются как
              восемь отдельных точек, 120 сотрудников — как плотная команда. */}
          <div className={`mt-3 flex flex-wrap ${g.count <= 12 ? 'gap-2' : 'gap-1.5'}`} aria-hidden>
            {Array.from({ length: g.count }, (_, i) => (
              <span
                key={i}
                className={`${g.count <= 12 ? 'h-8 w-8 rounded-[5px]' : 'h-3.5 w-3.5 rounded-[3px]'} ${FILL[g.tone ?? 'accent']}`}
              />
            ))}
          </div>
        </li>
      ))}
    </ul>
  )
}

function Waffle({ v }: { v: Extract<CaseVisual, { kind: 'waffle' }> }) {
  return (
    <div>
      <ul className="grid gap-6 min-[440px]:grid-cols-2">
        {v.grids.map((g) => {
          const full = Math.floor(g.value)
          const part = g.value - full
          return (
            <li key={g.label}>
              <div className={`${MONO} ${g.tone === 'accent' ? 'text-accent' : 'text-muted'}`}>{g.label}</div>
              <div className="mt-3 grid w-full max-w-[15rem] grid-cols-10 gap-1" aria-hidden>
                {Array.from({ length: 100 }, (_, i) => (
                  <span
                    key={i}
                    className={`aspect-square rounded-[2px] ${i < full ? FILL[g.tone ?? 'base'] : 'bg-divider'}`}
                    style={
                      i === full && part > 0
                        ? {
                            background: `linear-gradient(to right, rgb(var(--${g.tone === 'loss' ? 'danger' : 'accent'}-rgb)) ${Math.round(part * 100)}%, rgb(var(--divider-rgb)) 0)`,
                          }
                        : undefined
                    }
                  />
                ))}
              </div>
              <p className="mt-3 text-small text-ink leading-snug">{g.text}</p>
            </li>
          )
        })}
      </ul>
      <p className="mt-4 text-small text-muted">{v.caption}</p>
    </div>
  )
}

function Body({ v }: { v: CaseVisual }) {
  switch (v.kind) {
    case 'compare':
      return <Compare v={v} />
    case 'bars':
      return <Bars v={v} />
    case 'columns':
      return <Columns v={v} />
    case 'timeline':
      return <Timeline v={v} />
    case 'transform':
      return <Transform v={v} />
    case 'systems':
      return <Systems v={v} />
    case 'dialog':
      return <Dialog v={v} />
    case 'ledger':
      return <Ledger v={v} />
    case 'tiles':
      return <Tiles v={v} />
    case 'verify':
      return <Verify v={v} />
    case 'memo':
      return <Memo v={v} />
    case 'units':
      return <Units v={v} />
    case 'waffle':
      return <Waffle v={v} />
  }
}

/* ——————————————————————— обёртка ——————————————————————— */

export function CaseVisualFigure({ v, locale }: { v: CaseVisual; locale: Locale }) {
  return (
    <figure
      data-visual={v.kind}
      className="cv mt-10 -mx-3 rounded-card border border-divider bg-card/70 p-4 sm:-mx-5 sm:p-6 lg:-mx-12 lg:p-8 xl:-mx-16"
    >
      <figcaption>
        <div className={`flex items-center gap-2 ${MONO} text-muted`}>
          <span className="h-1.5 w-1.5 rounded-full bg-accent" aria-hidden />
          {v.kicker}
        </div>
        <div className="mt-2 font-serif text-h3 text-ink leading-snug">{v.title}</div>
        {v.ask && (
          <p className="mt-4 flex max-w-xl items-start gap-3 rounded-2xl rounded-tl-sm border border-divider bg-bg px-4 py-2.5 text-small text-ink">
            <span className={`${MONO} shrink-0 pt-[0.2rem] text-accent`}>{ASK[locale]}</span>
            <span className="leading-snug">{v.ask}</span>
          </p>
        )}
      </figcaption>
      <div className="mt-6">
        <Body v={v} />
      </div>
      {v.note && <p className="mt-6 border-t border-divider pt-4 text-small text-muted leading-relaxed">{v.note}</p>}
    </figure>
  )
}

/** Все иллюстрации одного блока страницы, в порядке данных. */
export function CaseVisuals({ items, slot, locale }: { items: CaseVisual[]; slot: VisualSlot; locale: Locale }) {
  const list = items.filter((v) => v.slot === slot)
  if (!list.length) return null
  return (
    <>
      {list.map((v) => (
        <CaseVisualFigure key={v.kicker + v.title} v={v} locale={locale} />
      ))}
    </>
  )
}
